import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  CheckCircle2,
  ChevronDown,
  Cctv,
  Loader2,
  RotateCcw,
  Wifi,
} from "lucide-react";
import { PageHeader } from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { API_BASE, api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function AddCameraPage() {
  const navigate = useNavigate();
  const [address, setAddress] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [onvifPort, setOnvifPort] = useState("");
  const [manualRtspUrl, setManualRtspUrl] = useState("");
  const [probing, setProbing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [probe, setProbe] = useState(null);
  const [name, setName] = useState("");
  const [error, setError] = useState("");

  const runProbe = async (event) => {
    event.preventDefault();
    setError("");
    setProbing(true);
    try {
      const response = await api.post("/ip-cameras/probe", {
        address: address.trim(),
        username: username.trim(),
        password,
        onvif_port: onvifPort ? Number(onvifPort) : null,
        manual_rtsp_url: manualRtspUrl.trim() || null,
      });
      setProbe(response.data);
      setName(response.data.suggested_name || `Caméra ${address.trim()}`);
      setPassword("");
      setManualRtspUrl("");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setProbing(false);
    }
  };

  const addCamera = async (event) => {
    event.preventDefault();
    if (!probe) return;
    setError("");
    setSaving(true);
    try {
      await api.post("/ip-cameras", {
        probe_id: probe.probe_id,
        name: name.trim(),
      });
      toast.success("Caméra ajoutée");
      navigate("/cameras", { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const resetProbe = () => {
    setProbe(null);
    setName("");
    setError("");
  };

  return (
    <div data-testid="add-camera-page">
      <PageHeader
        title="Ajouter une caméra"
        description="CAM essaie de détecter automatiquement le flux vidéo."
      />

      <div className="px-4 sm:px-6 lg:px-8 pb-12 max-w-3xl">
        {!probe ? (
          <form
            onSubmit={runProbe}
            autoComplete="off"
            className="rounded-2xl border border-white/[0.08] bg-white/[0.025] p-5 sm:p-7"
            data-testid="ip-camera-probe-form"
          >
            <div className="flex items-start gap-4 mb-7">
              <div className="size-11 shrink-0 rounded-2xl bg-brand/15 text-brand grid place-items-center">
                <Cctv className="size-5" strokeWidth={1.8} />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-white">Caméra IP</h2>
                <p className="mt-1 text-sm leading-relaxed text-zinc-400">
                  Indiquez simplement son adresse et ses identifiants. CAM cherchera le bon flux.
                </p>
              </div>
            </div>

            <div className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="camera-address" className="text-sm text-zinc-300">
                  Adresse IP ou nom d’hôte
                </Label>
                <Input
                  id="camera-address"
                  name="camera-address"
                  data-testid="ip-camera-address"
                  value={address}
                  onChange={(event) => setAddress(event.target.value)}
                  placeholder="192.168.1.42"
                  required
                  autoFocus
                  autoComplete="off"
                  className="h-11 rounded-xl border-white/[0.10] bg-black/20 text-white placeholder:text-zinc-600"
                />
                <p className="text-xs text-zinc-500">
                  Cette adresse doit être joignable depuis le serveur CAM.
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="camera-username" className="text-sm text-zinc-300">
                    Utilisateur
                  </Label>
                  <Input
                    id="camera-username"
                    name="camera-device-username"
                    data-testid="ip-camera-username"
                    value={username}
                    onChange={(event) => setUsername(event.target.value)}
                    placeholder="admin"
                    autoComplete="off"
                    className="h-11 rounded-xl border-white/[0.10] bg-black/20 text-white placeholder:text-zinc-600"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="camera-password" className="text-sm text-zinc-300">
                    Mot de passe
                  </Label>
                  <Input
                    id="camera-password"
                    name="camera-device-password"
                    data-testid="ip-camera-password"
                    type="password"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    autoComplete="new-password"
                    className="h-11 rounded-xl border-white/[0.10] bg-black/20 text-white"
                  />
                </div>
              </div>

              <details className="group rounded-xl border border-white/[0.07] bg-black/10">
                <summary
                  className="flex min-h-11 cursor-pointer list-none items-center justify-between gap-3 px-4 py-3 text-sm text-zinc-400 hover:text-zinc-200"
                  data-testid="ip-camera-advanced-toggle"
                >
                  Configuration avancée
                  <ChevronDown className="size-4 transition-transform group-open:rotate-180" strokeWidth={1.7} />
                </summary>
                <div className="border-t border-white/[0.06] px-4 py-4 space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="camera-onvif-port" className="text-sm text-zinc-300">
                      Port ONVIF
                    </Label>
                    <Input
                      id="camera-onvif-port"
                      name="camera-onvif-port"
                      data-testid="ip-camera-onvif-port"
                      type="number"
                      min="1"
                      max="65535"
                      value={onvifPort}
                      onChange={(event) => setOnvifPort(event.target.value)}
                      placeholder="Automatique"
                      autoComplete="off"
                      className="h-11 rounded-xl border-white/[0.10] bg-black/20 text-white placeholder:text-zinc-600"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="camera-rtsp-url" className="text-sm text-zinc-300">
                      URL RTSP
                    </Label>
                    <Input
                      id="camera-rtsp-url"
                      name="camera-rtsp-url"
                      data-testid="ip-camera-rtsp-url"
                      value={manualRtspUrl}
                      onChange={(event) => setManualRtspUrl(event.target.value)}
                      placeholder="rtsp://192.168.1.42:554/stream1"
                      autoComplete="off"
                      className="h-11 rounded-xl border-white/[0.10] bg-black/20 text-white placeholder:text-zinc-600"
                    />
                    <p className="text-xs text-zinc-500">
                      À utiliser uniquement si la détection automatique ne fonctionne pas.
                    </p>
                  </div>
                </div>
              </details>
            </div>

            {error && (
              <div
                className="mt-5 rounded-xl border border-red-500/20 bg-red-500/[0.06] px-4 py-3 text-sm leading-relaxed text-red-200"
                data-testid="ip-camera-error"
              >
                {error}
              </div>
            )}

            <div className="mt-7 flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-3">
              <button
                type="button"
                onClick={() => navigate("/cameras")}
                className="h-11 inline-flex items-center justify-center gap-2 rounded-xl px-3 text-sm font-medium text-zinc-400 hover:text-white hover:bg-white/[0.05] transition-colors"
                data-testid="add-camera-back"
              >
                <ArrowLeft className="size-4" strokeWidth={1.7} />
                Retour
              </button>

              <Button
                type="submit"
                disabled={probing || !address.trim()}
                className="h-11 rounded-xl bg-brand px-5 text-white hover:bg-brand-hover"
                data-testid="ip-camera-probe-submit"
              >
                {probing ? (
                  <>
                    <Loader2 className="size-4 mr-2 animate-spin" strokeWidth={1.8} />
                    Recherche…
                  </>
                ) : (
                  <>
                    <Wifi className="size-4 mr-2" strokeWidth={1.8} />
                    Détecter la caméra
                  </>
                )}
              </Button>
            </div>
          </form>
        ) : (
          <form
            onSubmit={addCamera}
            autoComplete="off"
            className="overflow-hidden rounded-2xl border border-white/[0.08] bg-white/[0.025]"
            data-testid="ip-camera-confirm-form"
          >
            <div className="aspect-video bg-black relative">
              <img
                src={`${API_BASE}/ip-cameras/probes/${probe.probe_id}/preview`}
                alt="Aperçu de la caméra"
                className="absolute inset-0 h-full w-full object-contain"
                data-testid="ip-camera-preview"
              />
              <div className="absolute left-4 top-4 inline-flex items-center gap-2 rounded-full bg-black/55 px-3 py-1.5 text-xs font-medium text-white backdrop-blur-sm">
                <CheckCircle2 className="size-3.5 text-emerald-400" strokeWidth={2} />
                Caméra trouvée
              </div>
            </div>

            <div className="p-5 sm:p-7">
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mb-6 text-sm">
                <span className="text-zinc-300">{probe.address}</span>
                <span className="text-zinc-500">{probe.protocol === "onvif" ? "ONVIF / RTSP" : "RTSP"}</span>
              </div>

              <div className="space-y-2">
                <Label htmlFor="camera-name" className="text-sm text-zinc-300">
                  Nom de la caméra
                </Label>
                <Input
                  id="camera-name"
                  name="camera-display-name"
                  data-testid="ip-camera-name"
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  minLength={3}
                  maxLength={60}
                  required
                  autoFocus
                  autoComplete="off"
                  className="h-11 rounded-xl border-white/[0.10] bg-black/20 text-white"
                />
              </div>

              {error && (
                <div
                  className="mt-5 rounded-xl border border-red-500/20 bg-red-500/[0.06] px-4 py-3 text-sm leading-relaxed text-red-200"
                  data-testid="ip-camera-error"
                >
                  {error}
                </div>
              )}

              <div className="mt-7 flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-3">
                <button
                  type="button"
                  onClick={resetProbe}
                  className="h-11 inline-flex items-center justify-center gap-2 rounded-xl px-3 text-sm font-medium text-zinc-400 hover:text-white hover:bg-white/[0.05] transition-colors"
                  data-testid="ip-camera-retry"
                >
                  <RotateCcw className="size-4" strokeWidth={1.7} />
                  Recommencer
                </button>

                <Button
                  type="submit"
                  disabled={saving || name.trim().length < 3}
                  className="h-11 rounded-xl bg-brand px-5 text-white hover:bg-brand-hover"
                  data-testid="ip-camera-add-submit"
                >
                  {saving ? (
                    <>
                      <Loader2 className="size-4 mr-2 animate-spin" strokeWidth={1.8} />
                      Ajout…
                    </>
                  ) : (
                    <>
                      <Cctv className="size-4 mr-2" strokeWidth={1.8} />
                      Ajouter la caméra
                    </>
                  )}
                </Button>
              </div>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
